import {useState, type FormEvent} from 'react'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'

/** Un acto con consecuencia legal exige motivo escrito, que un `confirm` genérico no puede
 * pedir. Todo su texto entra por props. */
export function MotivoDialog({
  triggerLabel,
  triggerTestId,
  title,
  description,
  motivoLabel,
  motivoTestId,
  submitLabel,
  onSubmit,
}: {
  triggerLabel: string
  triggerTestId: string
  title: string
  description: string
  motivoLabel: string
  motivoTestId: string
  submitLabel: string
  /** Si lanza, el diálogo queda abierto: cerrar sobre un fallo haría creer que el motivo se guardó. */
  onSubmit: (motivo: string) => Promise<unknown>
}) {
  const [open, setOpen] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [motivoError, setMotivoError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    const err = validateRequired(motivo)
    setMotivoError(err ?? undefined)
    if (err) return
    setError(null)
    setPending(true)
    try {
      await onSubmit(motivo)
      setOpen(false)
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          setMotivo('')
          setMotivoError(undefined)
          setError(null)
        }
      }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={triggerTestId}>
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">{description}</p>
          <label className="block space-y-1">
            <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {motivoLabel}
            </span>
            <Input
              value={motivo}
              /* El backend acota a 2000; más largo vuelve como un 422 de pydantic cuyo
                 `detail` es una lista, que `KNOWN_ERRORS` no mapea. */
              maxLength={2000}
              data-testid={motivoTestId}
              onChange={(e) => {
                setMotivo(e.target.value)
                setMotivoError(undefined)
              }}
              onBlur={(e) => setMotivoError(validateRequired(e.target.value) ?? undefined)}
            />
            <FieldError error={motivoError} />
          </label>
          <FormDialogFooter error={error} pending={pending} submitLabel={submitLabel} onCancel={() => setOpen(false)} />
        </form>
      </DialogContent>
    </Dialog>
  )
}
